import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { trackGtm } from '../../utils';

import Button from '../button/Button';
import IconBlocks from '../iconBlocks/IconBlocks';

import touchpad from '../../../content/media-library/icons/touchpad-finger.svg';
import stalingRounded from '../../../content/media-library/icons/starling-rounded-square.svg';
import checklist from '../../../content/media-library/icons/checklist.svg';
import selfie from '../../../content/media-library/icons/selfie.svg';

import * as styles from './OnboardingCta.module.scss';

const ROUTES = {
  default: {
    path: '/apply/',
    heading: 'Apply for a Starling bank account today and enjoy app-based banking at its best.'
  },
  pca: {
    path: '/apply/?r=pca',
    heading: 'Apply for a Starling current account today and enjoy app-based banking at its best.'
  },
  bca: {
    path: '/apply/?r=bca',
    heading: 'Apply for a Starling business account today and enjoy app-based banking at its best.'
  },
  sta: {
    path: '/apply/?r=sta',
    heading: 'Apply for a Starling sole trader account today and enjoy app-based banking at its best.'
  }
};

const OnboardingCta = ({
  theme = 'darkNavy',
  withSteps = true,
  trackingId,
  route = 'default',
  heading,
  textOnly = false,
  displayHeadingAsH2 = false
}) => {
  const selectedRoute = ROUTES[route] || ROUTES['default'];
  const displaySteps = typeof withSteps === 'string' ? withSteps === 'true' : withSteps;
  const displayTextOnly = typeof textOnly === 'string' ? textOnly === 'true' : textOnly;

  return (
    <div
      id="how-to-open"
      className={classnames(styles.container, {
        [styles[`route__${route}`]]: route,
        [styles[`theme__${theme}`]]: theme,
        [styles.withoutSteps]: !displaySteps,
        [styles.textOnly]: displayTextOnly
      })}
    >
      {displayHeadingAsH2 ? (
        <h2 className="as-h3" dangerouslySetInnerHTML={{ __html: heading || selectedRoute.heading }} />
      ) : (
        <h3 dangerouslySetInnerHTML={{ __html: heading || selectedRoute.heading }} />
      )}

      {displaySteps ? (
        <IconBlocks
          customPadding="no-top-bottom"
          compact={true}
          slider={true}
          blocks={[
            {
              img: {
                src: touchpad,
                alt: 'Click the button'
              },
              title: 'Step 1',
              text: 'Simply click on the button below to start your application and fill in a quick, fuss-free form'
            },
            {
              img: {
                src: stalingRounded,
                alt: 'Download the app'
              },
              title: 'Step 2',
              text: 'Download the app on your phone and we’ll ping over a verification code for you to enter'
            },
            {
              img: {
                src: checklist,
                alt: 'Create an account'
              },
              title: 'Step 3',
              text: 'Create an account by telling us a bit about yourself: your name, date of birth, email address and current home address'
            },
            {
              img: {
                src: selfie,
                alt: 'Record a quick selfie'
              },
              title: 'Step 4',
              text: 'Finally, record a quick selfie video and take a photo of your ID so we can verify everything. And that’s it!'
            }
          ]}
        />
      ) : null}
      <Button
        className={classnames('btn-download-app', styles.startCta)}
        asLink={true}
        variant="primary"
        href={selectedRoute.path}
        data-tracking-id={trackingId}
        onClick={() => trackGtm({ event: 'onboarding_cta_click', placement: 'onboarding_cta_banner' })}
      >
        Start your application
      </Button>
    </div>
  );
};

OnboardingCta.propTypes = {
  theme: PropTypes.oneOf(['darkNavy', 'neutralNavy', 'lightGrey', 'sand', 'white']),
  withSteps: PropTypes.oneOfType([PropTypes.bool, PropTypes.oneOf(['true', 'false'])]),
  trackingId: PropTypes.string.isRequired,
  route: PropTypes.oneOf(Object.keys(ROUTES)),
  heading: PropTypes.string,
  textOnly: PropTypes.oneOfType([PropTypes.bool, PropTypes.oneOf(['true', 'false'])]),
  displayHeadingAsH2: PropTypes.oneOfType(PropTypes.bool)
};

export default OnboardingCta;
